/**
 * Cabin-class taxonomy — single source of truth for the four-cabin order used
 * by the fare-family matrix, SRP badges and (eventually) itinerary/seat-map
 * theming. See docs/PREMIUM_ECONOMY_DESKTOP_ARCHITECTURE.md §5.1.
 *
 * Deliberately re-exports `CabinTabKey` (already shipped in Phase 1) as
 * `CabinClassType` rather than redefining a parallel enum — desktop had no
 * prior cabin taxonomy module, so this is additive, not a migration.
 *
 * Tab identity can also be an airline sub-product (`displayCabinType`, e.g.
 * Indigo Up Front) outside the four cabins — theming stays on CabinClassType.
 */
import { CABIN_TAB_OPTIONS, CabinTabKey } from './premiumEconomy'

export type CabinClassType = CabinTabKey

/** Theme cabins only — ECONOMY | PREMIUM_ECONOMY | BUSINESS | FIRST. */
export type FareCabinTabId = CabinClassType

/**
 * Tab identity from `displayCabinType`. May be a canonical cabin or a
 * sub-product (e.g. INDIGO_UPFRONT) that still wears its cabinType theme.
 */
export type CabinTabId = FareCabinTabId | string

export const ECONOMY: CabinClassType = 'ECONOMY'
export const PREMIUM_ECONOMY: CabinClassType = 'PREMIUM_ECONOMY'
export const BUSINESS: CabinClassType = 'BUSINESS'
export const FIRST: CabinClassType = 'FIRST'

/** Indigo Up Front — Economy sub-product with its own fare-board tab. */
export const INDIGO_UPFRONT_CABIN = 'INDIGO_UPFRONT'

/**
 * Fallback theme when a display-cabin tab has no fares yet (loading / summary-only).
 * Sub-products map to the cabin they sell under.
 */
export const DISPLAY_CABIN_THEME: Record<string, FareCabinTabId> = {
    [INDIGO_UPFRONT_CABIN]: ECONOMY,
}

/** Drives 4-cabin theme/order everywhere (matrix ranks, badges, theming lookups). */
export const CABIN_ORDER: CabinClassType[] = [ECONOMY, PREMIUM_ECONOMY, BUSINESS, FIRST]

/** enum -> display label, e.g. PREMIUM_ECONOMY -> 'Premium Economy'. */
export const cabinClassToLabel: Record<CabinClassType, string> = CABIN_TAB_OPTIONS.reduce(
    (acc, { key, label }) => ({ ...acc, [key]: label }),
    {} as Record<CabinClassType, string>
)

/** Explicit sub-product labels (generic title-case would also work for INDIGO_UPFRONT). */
const CABIN_TAB_LABELS: Record<string, string> = {
    ...cabinClassToLabel,
    [INDIGO_UPFRONT_CABIN]: 'Indigo Upfront',
}

/** display label -> enum (reverse map), e.g. 'Premium Economy' -> PREMIUM_ECONOMY. */
export const labelToCabinClass: Record<string, CabinClassType> = CABIN_TAB_OPTIONS.reduce(
    (acc, { key, label }) => ({ ...acc, [label]: key }),
    {} as Record<string, CabinClassType>
)

/** Tab strip label for a cabin / sub-product id. */
export const getCabinTabLabel = (tabId: string): string =>
    CABIN_TAB_LABELS[tabId] ||
    tabId
        .split('_')
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')

export const normalizeCabinId = (cabin?: string | null): string =>
    (cabin || '').trim().toUpperCase().replace(/[\s-]+/g, '_')

export const isIndigoUpfrontCabin = (cabin?: string | null): boolean =>
    normalizeCabinId(cabin) === INDIGO_UPFRONT_CABIN
